import { useMutation } from "@apollo/client"
import {
  PureQueryOptions,
  MutationUpdaterFn,
  ApolloError,
} from "@apollo/client"
import React, { useState } from "react"
import CustomButton, { CustomButtonProps } from "../Atoms/CustomButton"
import AppToaster from "utils/AppToaster"
import { DocumentNode } from "graphql"
import CustomAlert from "../Atoms/CustomAlert"
import { AlertProps } from "@blueprintjs/core"
// Component Name - ButtonMutation
// Component Used For - handling mutations through a button
// Author - Malik Bagwala

/*
Props
1. mutation - the mutation specified as a variable wrappen in gql tags
2. variables - an object including all the variables required by the mutation
2. onData - the callback which is called on a successful mutation
3. onError - the callback which is called on a failed mutation
4. validate - the function that runs before hitting the mutation if it runs true then the mutation is carried out (default true)
4. update - the callback which allows the user to manually update the apollo cache
5. refetchQueries - an array of queries to automatically update the apollo cache by refetching those queries


Components Used
1. CustomButton
*/
export interface ConfirmButtonMutationProps
  extends CustomButtonProps,
    Omit<AlertProps, "isOpen" | "onClose"> {
  mutation: DocumentNode
  onCompleted?: (data: any) => void | undefined
  onError?: (error: ApolloError) => void | undefined
  variables?: Record<string, any> | undefined
  update?: MutationUpdaterFn<any> | undefined
  optimisticResponse?: any
  validate?: (data: any) => boolean
  refetchQueries?: (string | PureQueryOptions)[] | undefined
  /**
   * Confirm alert contents
   * eg. <p> Are you sure you want to proceed? </p>
   */
  alert?: any
  addConfirmation?: any
}
const ConfirmButtonMutation = ({
  mutation,
  onCompleted,
  onError,
  variables,
  update,
  refetchQueries,
  text,
  children,
  validate,
  optimisticResponse,
  onClick,
  alert,
  confirmButtonText,
  addConfirmation,
  ...CustomButtonProps
}: ConfirmButtonMutationProps) => {
  const [isOpen, setOpen] = useState(false)
  const [mutationFunction, { loading }] = useMutation(mutation, {
    onError,
    onCompleted,
  })

  function handleClick(e: React.MouseEvent<HTMLElement, MouseEvent>) {
    if (addConfirmation) {
      setOpen(true)
    } else {
      mutate()
    }
  }

  function mutate() {
    if (validate && validate(variables)) {
      if (!loading) {
        mutationFunction({
          variables,

          update: (cache: any, result: any) => {
            update && update(cache, result)
          },
          optimisticResponse,
          refetchQueries,
        })
      }
    }
  }
  return (
    <>
      <CustomButton
        id="vt-custom-button-mutation"
        disabled={loading}
        loading={loading}
        onClick={handleClick}
        {...CustomButtonProps}
      >
        {text || children}
      </CustomButton>
      <CustomAlert
        intent="warning"
        icon="warning-sign"
        confirmButtonText={confirmButtonText}
        cancelButtonText="Cancel"
        isOpen={isOpen}
        onClose={confirmed => {
          if (confirmed) {
            mutate()
          }
          setOpen(false)
        }}
      >
        {alert}
      </CustomAlert>
    </>
  )
}

ConfirmButtonMutation.defaultProps = {
  dark: true,
  onError({ graphQLErrors, networkError }) {
    graphQLErrors.forEach(({ message }) => {
      AppToaster.show({
        message: message || "Something went wrong please try again",
        intent: "danger",
        icon: "cross",
      })
    })
    if (networkError) {
      AppToaster.show({
        message: networkError.message,
        intent: "danger",
        icon: "cross",
      })
    }
  },
  validate: () => true,
  confirmButtonText: "Confirm",
  addConfirmation: true,
}
export default ConfirmButtonMutation
